import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';

// ── Public types ──────────────────────────────────────────────────────────────

export interface GltfLoaderConfig {
  readonly dracoDecoderPath: string; // e.g. '/jsm/libs/draco/gltf/'
  readonly ktx2TranscoderPath: string; // e.g. '/jsm/libs/basis/'
  /** Shared renderer for KTX2.detectSupport(). Caller (Plan 10) injects this. */
  readonly renderer: THREE.WebGLRenderer;
  /** OPTIONAL pre-built decoder instance for sharing across multiple loaders.
   *  When provided, factory skips construction + DOES NOT own disposal.
   *  When absent, factory constructs + owns disposal in dispose().
   */
  readonly ktx2Loader?: KTX2Loader;
  readonly dracoLoader?: DRACOLoader;
  /** Optional Meshopt decoder for EXT_meshopt_compression animation/model bundles. */
  readonly meshoptDecoder?: unknown;
}

export interface GltfAssetLoader {
  /** Load and parse a GLB. Same-origin URL. Resolves to THREE.Group. */
  load(url: string): Promise<THREE.Group>;
  /** Load and parse a GLB, preserving the animation clips for rigged assets. */
  loadAsset(url: string): Promise<{ scene: THREE.Group; animations: THREE.AnimationClip[] }>;
  /** Dispose OWNED decoder workers. Does NOT dispose caller-injected instances. */
  dispose(): void;
}

export class GltfLoaderError extends Error {
  constructor(
    public readonly reason: 'fetch_failed' | 'parse_failed' | 'decoder_not_configured',
    public readonly detail: string,
    public readonly url: string,
  ) {
    super(`GltfLoaderError(${reason}, ${url}): ${detail}`);
    this.name = 'GltfLoaderError';
  }
}

// ── Error classification ──────────────────────────────────────────────────────

/**
 * Classify a raw loader error into a typed GltfLoaderError.
 * - Message containing "fetch", "404", "403", or "5xx"-style HTTP codes → fetch_failed
 * - Everything else → parse_failed
 */
export function classifyGltfError(err: unknown, url: string): GltfLoaderError {
  const message = err instanceof Error ? err.message : String(err);

  // HTTP status codes: 4xx and 5xx, or the word "fetch"
  const fetchPattern = /fetch|40[0-9]|4[1-9][0-9]|5[0-9]{2}/i;
  if (fetchPattern.test(message)) {
    // Extract HTTP status code if present
    const statusMatch = /[45][0-9]{2}/.exec(message);
    const detail = statusMatch != null ? `HTTP ${statusMatch[0]}` : message;
    return new GltfLoaderError('fetch_failed', detail, url);
  }

  return new GltfLoaderError('parse_failed', message, url);
}

// ── Factory ───────────────────────────────────────────────────────────────────

/** Factory. Constructs a GltfAssetLoader bound to the given config. */
export function createGltfAssetLoader(config: GltfLoaderConfig): GltfAssetLoader {
  // Validate: dracoDecoderPath required when no injected dracoLoader
  if (config.dracoLoader === undefined && config.dracoDecoderPath === '') {
    throw new GltfLoaderError(
      'decoder_not_configured',
      'dracoDecoderPath must be non-empty when no dracoLoader is injected',
      '',
    );
  }

  // Validate: ktx2TranscoderPath required when no injected ktx2Loader
  if (config.ktx2Loader === undefined && config.ktx2TranscoderPath === '') {
    throw new GltfLoaderError(
      'decoder_not_configured',
      'ktx2TranscoderPath must be non-empty when no ktx2Loader is injected',
      '',
    );
  }

  // Ownership tracking — factory only disposes what it constructed
  const ownedKtx2 = config.ktx2Loader === undefined;
  const ownedDraco = config.dracoLoader === undefined;

  // Set up KTX2 decoder
  const ktx2 = config.ktx2Loader ?? (() => {
    const loader = new KTX2Loader();
    loader.setTranscoderPath(config.ktx2TranscoderPath);
    loader.detectSupport(config.renderer);
    return loader;
  })();

  // Set up Draco decoder
  const draco = config.dracoLoader ?? (() => {
    const loader = new DRACOLoader();
    loader.setDecoderPath(config.dracoDecoderPath);
    return loader;
  })();

  // Wire decoders into the GLTF loader
  const gltfLoader = new GLTFLoader();
  gltfLoader.setDRACOLoader(draco);
  gltfLoader.setKTX2Loader(ktx2);
  if (config.meshoptDecoder != null) {
    gltfLoader.setMeshoptDecoder(config.meshoptDecoder);
  }

  return {
    loadAsset(url: string): Promise<{ scene: THREE.Group; animations: THREE.AnimationClip[] }> {
      return new Promise((resolve, reject) => {
        gltfLoader.load(
          url,
          (gltf) => {
            resolve({ scene: gltf.scene, animations: gltf.animations });
          },
          undefined,
          (err) => {
            reject(classifyGltfError(err, url));
          },
        );
      });
    },

    load(url: string): Promise<THREE.Group> {
      return this.loadAsset(url).then((asset) => asset.scene);
    },

    dispose(): void {
      if (ownedKtx2) ktx2.dispose();
      if (ownedDraco) draco.dispose();
    },
  };
}
